import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { PageTitle } from 'ui/PageTitle';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import FluidButton from 'ui/FluidButton';
import { BackLink } from 'ui/BackLink';
import { AnnouncementEditForm } from './components/AnnouncementEditForm';
import { isAdmin } from 'store/modules/auth/selectors';
import { useAnnouncementEdit } from './hooks/useAnnouncementEdit';

export const AnnouncementCreatePage = () => {
  const history = useHistory();
  const isAdminUser = useSelector(isAdmin);

  // Use the announcement edit hook for state management
  const {
    announcementEditState,
    isEditedWithoutSaving,
    isLoading,
    error,
    validationErrors,
    handleSave,
    handleCancel,
    updateHotelUuid,
    updateTitle,
    updateDescription,
    updateRestrictType,
    updateCountryCodes,
    resetCountryCodes,
    updateTravelCompanyUuids,
    updateIsDraft,
    updatePublishFor,
    updateActiveFrom,
    updateActiveTo,
    updateAppearIn,
  } = useAnnouncementEdit({ announcementUuid: null });

  // Redirect to announcements list if user is not an admin
  useEffect(() => {
    if (!isAdminUser) {
      history.push('/announcements');
    }
  }, [isAdminUser, history]);

  // Browser beforeunload event for unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditedWithoutSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditedWithoutSaving]);

  if (isLoading) {
    return <LoadingBar />;
  }

  if (error) {
    return <ErrorBar />;
  }

  return (
    <div className="announcement-create w-auto mx-[20px] crm-desktop:mx-[80px]">
      <BackLink type="internal" href="/announcements">
        Back to announcements
      </BackLink>
      <PageTitle primaryText="New Announcement" />

      <div className="announcement-create-form mt-[30px]">
        <AnnouncementEditForm
          announcementEditState={announcementEditState}
          validationErrors={validationErrors}
          isCreateMode={true}
          updateHotelUuid={updateHotelUuid}
          updateTitle={updateTitle}
          updateDescription={updateDescription}
          updateRestrictType={updateRestrictType}
          updateCountryCodes={updateCountryCodes}
          resetCountryCodes={resetCountryCodes}
          updateTravelCompanyUuids={updateTravelCompanyUuids}
          updateIsDraft={updateIsDraft}
          updatePublishFor={updatePublishFor}
          updateActiveFrom={updateActiveFrom}
          updateActiveTo={updateActiveTo}
          updateAppearIn={updateAppearIn}
        />
      </div>

      <div className="actions-bar mt-[30px] flex gap-[15px]">
        <FluidButton type="primary" onClick={handleSave}>
          Save
        </FluidButton>
      </div>

      <LeaveWithoutSavingModal when={isEditedWithoutSaving} />
    </div>
  );
};
